import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Banknote, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ConnectStripeButton from "../components/ConnectStripeButton";
import UnlockConfetti from "../components/UnlockConfetti";
import CaulTopupCard from "../components/CaulTopupCard";
import EscrowStatusPill from "../components/EscrowStatusPill";
import IntakeAnswersBlock from "../components/IntakeAnswersBlock";
import XamanConnect from "../components/XamanConnect";
import XamanTrustline from "../components/XamanTrustline";
import FounderWallStatsCard from "../components/FounderWallStatsCard";
import ReviewPromptDialog from "../components/ReviewPromptDialog";
import CaulPriceTickerBadge from "../components/CaulPriceTickerBadge";
import { mobileFlags } from "../lib/mobileFeatureFlags";

export default function Wallet() {
  const [data, setData] = useState({ wallet: null, transactions: [] });
  const [bookings, setBookings] = useState([]);
  const [lockups, setLockups] = useState(null);
  const [unlockEvents, setUnlockEvents] = useState([]);
  const [xrplAddress, setXrplAddress] = useState(null);
  const [trustlineActive, setTrustlineActive] = useState(false);
  const [caulOnChain, setCaulOnChain] = useState(null);
  const { user } = useAuth();
  const load = async () => {
    const [w, b, lk, ue, xw] = await Promise.all([
      api.get("/wallet"),
      api.get("/bookings"),
      api.get("/rewards/lockups").catch(() => ({ data: null })),
      api.get("/rewards/unlock-events", { params: { limit: 25 } }).catch(() => ({ data: [] })),
      // Role-agnostic Xaman wallet status — survives refresh for ALL roles,
      // not just providers.
      api.get("/caul-xrpl/my-wallet").catch(() => ({ data: null })),
    ]);
    setData(w.data);
    setBookings(b.data);
    setLockups(lk.data);
    setUnlockEvents(ue.data || []);
    const addr = xw?.data?.xrplAddress || null;
    setXrplAddress(addr);
    setTrustlineActive(Boolean(xw?.data?.trustlineActive));
    if (addr) {
      try {
        const { data: bal } = await api.get(`/caul-xrpl/balance/${addr}`);
        setCaulOnChain(bal);
      } catch (e) { /* ignore */ }
    }
  };
  useEffect(() => { load(); }, []);

  const markComplete = async (bid) => {
    try {
      await api.post(`/bookings/${bid}/complete`);
      toast.success("Session marked complete — client can now leave a review");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const earnings = bookings.reduce((s, b) => s + (b.providerEarnings || 0), 0);
  const spent = bookings.reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <Layout>
      <UnlockConfetti events={unlockEvents} />
      <ReviewPromptDialog />
      <div className="space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-mystic">Treasury</div>
            <h1 className="font-serif text-5xl mt-2">Wallet & Earnings</h1>
            <div className="mt-3">
              <CaulPriceTickerBadge />
            </div>
          </div>
          <Link to="/cashout" className={`bg-primary text-primary-foreground px-5 py-3 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2 hover:bg-primary/90 ${mobileFlags.cryptoCashoutsEnabled ? "" : "hidden"}`} data-testid="wallet-cashout-link">
            <Banknote size={14} strokeWidth={1.5} /> Cash Out CAUL → USD <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>

        {(user?.role === "PROVIDER" || user?.role === "ADMIN") && (
          <FounderWallStatsCard />
        )}

        {(user?.role === "PROVIDER" || user?.role === "ADMIN") && (
          <ConnectStripeButton providerId={user.id} isOwner={true} />
        )}

        {/* Xaman wallet is available to ALL roles — providers earn on-chain
            CAUL via payouts and ALL users need it to top up via the Xaman
            (XRP) tab. The backend stores the address on provider_profiles
            regardless of role. Hidden on iOS (Apple 3.1.5b - crypto). */}
        {mobileFlags.xrplFeaturesEnabled && (
        <div className="space-y-2" data-testid="xrpl-section">
          <XamanConnect currentAddress={xrplAddress} onLinked={(addr) => { setXrplAddress(addr); load(); }} />
          {xrplAddress && (
            <div className="text-right">
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Disconnect your Xaman wallet? Your $CAUL stays safe. Reconnect anytime — or connect a different wallet.")) return;
                  try {
                    await api.post("/caul-xrpl/my-wallet/disconnect");
                    setXrplAddress(null);
                    setTrustlineActive(false);
                    setCaulOnChain(null);
                    toast.success("Xaman wallet disconnected");
                  } catch (e) {
                    toast.error("Could not disconnect — try again.");
                  }
                }}
                className="text-[11px] uppercase tracking-[0.18em] text-foreground/45 hover:text-destructive"
                data-testid="xaman-disconnect-btn"
              >
                Disconnect wallet
              </button>
            </div>
          )}
          {xrplAddress && !trustlineActive && !caulOnChain?.hasTrustline && (
            <div className="parchment p-5 space-y-3" data-testid="trustline-open-card">
              <div>
                <div className="label-mystic">Step 2 · Open trustline</div>
                <h3 className="font-serif text-2xl mt-1">Allow CAUL on your wallet</h3>
                <p className="text-foreground/65 text-sm mt-2 max-w-md">
                  One Xaman signature opens a CAUL trustline (limit 10,000,000 CAUL).
                  After this, we can drop real on-chain CAUL into your wallet whenever
                  you earn it.
                </p>
              </div>
              <XamanTrustline onConfirmed={() => load()} />
            </div>
          )}
          {xrplAddress && (trustlineActive || caulOnChain?.hasTrustline) && (
            <div className="parchment p-4 flex items-center justify-between" data-testid="caul-onchain-balance">
              <div>
                <div className="label-mystic">On-chain CAUL</div>
                <div className="text-xs text-secondary mt-1 inline-flex items-center gap-1" data-testid="trustline-active-badge">
                  Trustline open · ready to receive · limit {caulOnChain?.limit || "10000000"}
                </div>
              </div>
              <div className="font-serif text-3xl text-secondary">{caulOnChain?.balance || "0"}</div>
            </div>
          )}
        </div>
        )}

        {lockups && lockups.totalLocked > 0 && (
          <div className="parchment p-4 border-primary/30 bg-primary/[0.04] flex items-center justify-between gap-3 flex-wrap" data-testid="wallet-lockup-banner">
            <div className="flex-1 min-w-[220px]">
              <div className="label-mystic text-primary">Bonus CAUL Locked</div>
              <div className="font-serif text-xl mt-1">{lockups.stillLocked} <span className="text-foreground/50 text-sm">of {lockups.totalLocked} CAUL</span></div>
              <div className="text-xs text-foreground/60 mt-1">100 CAUL unlocks per paid transaction · {lockups.exportLockDays}-day export gate before it reaches your liquid wallet.</div>
            </div>
            <Link to="/cashout" className={`text-secondary hover:text-secondary/80 text-xs uppercase tracking-wider ${mobileFlags.cryptoCashoutsEnabled ? "" : "hidden"}`} data-testid="wallet-lockup-details">View Details →</Link>
          </div>
        )}

        {mobileFlags.caulTopupsEnabled && (
        <CaulTopupCard
          onCredited={load}
          xrplAddress={xrplAddress}
          trustlineActive={trustlineActive || !!caulOnChain?.hasTrustline}
        />
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="parchment p-6">
            <div className="label-mystic">Cauldron Reward Token</div>
            <div className="font-serif text-5xl text-secondary mt-2" data-testid="wallet-balance">{data.wallet?.balance ?? 0}</div>
            <div className="text-xs text-foreground/60">CAUL · 1 CAUL ≈ $0.01</div>
          </div>
          {(user?.role === "PROVIDER" || user?.role === "ADMIN") && (
            <div className="parchment p-6">
              <div className="label-mystic">Earnings (USD)</div>
              <div className="font-serif text-5xl text-secondary mt-2" data-testid="wallet-earnings">${earnings.toFixed(2)}</div>
              <div className="text-xs text-foreground/60">Net provider earnings</div>
            </div>
          )}
          {user?.role !== "PROVIDER" && user?.role !== "ADMIN" && (
            <div className="parchment p-6">
              <div className="label-mystic">Spent</div>
              <div className="font-serif text-5xl text-secondary mt-2">${spent.toFixed(2)}</div>
            </div>
          )}
          <div className="parchment p-6">
            <div className="label-mystic">Bookings</div>
            <div className="font-serif text-5xl text-secondary mt-2" data-testid="wallet-bookings-count">{bookings.length}</div>
          </div>
        </div>

        <div>
          <div className="label-mystic mb-3">Token Ledger</div>
          <div className="parchment divide-y divide-secondary/15" data-testid="wallet-txns">
            {data.transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-4">
                <div>
                  <div className="text-sm">{t.description}</div>
                  <div className="label-mystic text-[10px] mt-1">{t.type} · {new Date(t.createdAt).toLocaleString()}</div>
                </div>
                <div className={`font-serif text-2xl ${t.amount >= 0 ? "text-secondary" : "text-destructive"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</div>
              </div>
            ))}
            {data.transactions.length === 0 && <div className="p-6 text-foreground/50 italic">No transactions yet.</div>}
          </div>
        </div>

        <div>
          <div className="label-mystic mb-3">Bookings</div>
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className="parchment p-4 flex flex-col gap-3" data-testid={`booking-${b.id}`}>
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="font-serif text-lg">{b.service?.title}</div>
                    <div className="label-mystic">{(user?.role === "PROVIDER" || user?.role === "ADMIN") ? `Client: ${b.client?.name}` : `with ${b.provider?.name}`} · {new Date(b.createdAt).toLocaleDateString()}</div>
                    {b.scheduledAt && (
                      <div className="text-[11px] text-secondary mt-1" data-testid={`booking-scheduled-${b.id}`}>
                        📅 {new Intl.DateTimeFormat(undefined, {
                          weekday: "short", month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit",
                        }).format(new Date(b.scheduledAt))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {b.status === "scheduled" && b.scheduledAt && (
                      <button
                        onClick={async () => {
                          const reason = window.prompt("Reason for cancellation (optional)");
                          if (reason === null) return;
                          try {
                            await api.post(`/bookings/${b.id}/cancel`, { reason });
                            toast.success("Booking cancelled");
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
                        }}
                        className="border border-destructive/45 text-destructive px-3 py-2 rounded-sm uppercase tracking-wider text-[11px] hover:bg-destructive/10"
                        data-testid={`booking-cancel-${b.id}`}
                      >
                        Cancel
                      </button>
                    )}
                    {(user?.role === "PROVIDER" || user?.role === "ADMIN") && b.status !== "completed" && (
                      <button
                        onClick={() => markComplete(b.id)}
                        className="border border-secondary text-secondary px-4 py-2 rounded-sm uppercase tracking-wider text-xs hover:bg-secondary/10"
                        data-testid={`booking-complete-${b.id}`}
                      >
                        Mark Complete
                      </button>
                    )}
                    <div className="text-right">
                      <div className="font-serif text-xl text-secondary">${(user?.role === "PROVIDER" || user?.role === "ADMIN") ? b.providerEarnings?.toFixed(2) : b.amount?.toFixed(2)}</div>
                      <div className="label-mystic text-[10px]">{b.status}{b.hasReview ? " · reviewed" : ""}</div>
                      <Link
                        to={`/bookings/${b.id}`}
                        className="text-[10px] uppercase tracking-wider text-secondary hover:underline mt-1 inline-block"
                        data-testid={`booking-details-${b.id}`}
                      >
                        View details →
                      </Link>
                    </div>
                  </div>
                </div>
                <EscrowStatusPill booking={b} viewerRole={user?.role} onUpdated={load} />
                {(user?.role === "PROVIDER" || user?.role === "ADMIN") && (
                  <IntakeAnswersBlock booking={b} />
                )}
              </div>
            ))}
            {bookings.length === 0 && <div className="parchment p-6 text-foreground/50 italic">No bookings yet.</div>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
